import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";
import ChairNode, { FloorLegend } from "@/components/ChairNode";
import AppointmentSession from "@/components/AppointmentSession";
import VideoEvidenceModal from "@/components/VideoEvidenceModal";
import SeatHistoryModal from "@/components/SeatHistoryModal";
import ServiceTimeline from "@/components/ServiceTimeline";
import { PlayCircle, Search, ArrowLeft, Plus, Radio } from "lucide-react";
import StitchingDemo from "@/components/StitchingDemo";

const BACK_MAP = {
  network: { to: "/network", label: "Network" },
  locations: { to: "/locations", label: "Locations" },
  reconciliation: { to: "/reconciliation", label: "Reconciliation" },
  dashboard: { to: "/", label: "Dashboard" },
};

export default function FloorView() {
  const { money, moneyFor, branding } = useBranding();
  const [storeId, setStoreId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [seatChair, setSeatChair] = useState(null);
  const [demoFlags, setDemoFlags] = useState([]);
  const onDemoLeak = (f) => setDemoFlags((p) => [{ ...f, id: `demo-${Date.now()}`, _demo: true, flagged: true }, ...p].slice(0, 3));
  const [timelineEv, setTimelineEv] = useState(null);
  const [storeSearch, setStoreSearch] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const back = BACK_MAP[searchParams.get("from")];
  const qc = useQueryClient();

  const refreshFloor = () => {
    qc.invalidateQueries({ queryKey: ["store", storeId] });
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["seat-history"] });
  };

  const addChair = async () => {
    if (!storeId) return;
    try {
      await api.post(`/stores/${storeId}/chairs`, {});
      toast.success("Chair added to the floor");
      refreshFloor();
    } catch { toast.error("Could not add a chair — check your access to this store"); }
  };

  const { data: stores } = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get("/stores").then((r) => r.data),
  });

  const { data: thresholds } = useQuery({
    queryKey: ["thresholds"],
    queryFn: () => api.get("/thresholds").then((r) => r.data),
  });
  const stage1Min = thresholds?.stage1_min ?? 6;

  const filteredStores = (stores || []).filter((s) => {
    const q = storeSearch.trim().toLowerCase();
    if (!q) return true;
    return s.name.toLowerCase().includes(q) || String(s.number).toLowerCase().includes(q);
  });

  // Deep-link support: /floor?store=<number|id> selects that store; otherwise default to first.
  useEffect(() => {
    if (!stores?.length) return;
    const want = searchParams.get("store");
    if (want) {
      const match = stores.find((s) => String(s.number) === want || s.id === want);
      if (match) { setStoreId(match.id); return; }
    }
    if (!storeId) setStoreId(stores[0].id);
  }, [stores, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: store } = useQuery({
    queryKey: ["store", storeId],
    queryFn: () => api.get(`/stores/${storeId}`).then((r) => r.data),
    enabled: !!storeId,
    refetchInterval: 4000,
  });

  const { data: sim, refetch: refetchSim } = useQuery({
    queryKey: ["sim-status"],
    queryFn: () => api.get("/sim/stream/status").then((r) => r.data),
    refetchInterval: 5000,
  });
  const toggleStream = async () => {
    if (!store?.number) return;
    try {
      if (sim?.running) { await api.post("/sim/stream/stop"); toast.info("Live sensor stream stopped"); }
      else { await api.post("/sim/stream/start", { store_number: store.number }); toast.success("Live sensor stream started — watch the floor"); }
      refetchSim();
    } catch { toast.error("Corporate access required"); }
  };

  const { data: flagged } = useQuery({
    queryKey: ["store-flagged", storeId],
    queryFn: () => api.get("/events", { params: { store_id: storeId, flagged: true, limit: 20 } }).then((r) => r.data),
    enabled: !!storeId,
    refetchInterval: 4000,
  });

  const ancillaryLabels = new Set((store?.chairs || []).filter((c) => c.role === "ancillary").map((c) => c.label));
  const visibleFlags = [...demoFlags, ...(flagged || []).filter((ev) => !ancillaryLabels.has(ev.chair_label))];

  return (
    <div>
      {back && (
        <button
          onClick={() => navigate(back.to)}
          data-testid="floor-back-chip"
          className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#1E2638] px-3 py-1.5 text-xs text-slate-300 hover:border-[#38BDF8] hover:text-white transition-colors duration-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {back.label}
        </button>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Live Floor View</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
            {store ? `Store #${store.number} — ${store.name}` : "Loading floor…"}
          </h1>
        </div>
        <Select value={storeId || ""} onValueChange={setStoreId}>
          <SelectTrigger className="w-64 bg-[#1E2638] border-[#2A3550]" data-testid="floor-store-select">
            <SelectValue placeholder="Select store" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E2638] border-[#2A3550]">
            <div className="p-2 sticky top-0 z-10 bg-[#1E2638] border-b border-[#2A3550]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search by name or number…"
                  data-testid="store-search-input"
                  className="w-full rounded-md bg-[#171E2E] border border-[#2A3550] pl-8 pr-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#38BDF8]"
                />
              </div>
            </div>
            {filteredStores.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`store-option-${s.number}`}>
                #{s.number} · {s.name}
              </SelectItem>
            ))}
            {filteredStores.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-400 text-center" data-testid="store-search-empty">
                No stores match “{storeSearch}”
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6"><StitchingDemo onLeak={onDemoLeak} /></div>

      <button onClick={toggleStream} data-testid="manage-stream-pill"
        className={`mt-6 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${sim?.running ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-[#2A3550] bg-[#1E2638] text-slate-300 hover:border-[#38BDF8]"}`}>
        <Radio className="h-4 w-4" /> {sim?.running ? "Live sensor stream running — tap to stop" : "Start a live sensor stream"}
      </button>

      {store?.mode === "salon" && <AppointmentSession storeId={storeId} />}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Floor map */}
        <div className="lg:col-span-2 surface-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg font-medium">Chair map</h2>
            <div className="flex items-center gap-4">
              <FloorLegend />
              <button onClick={addChair} data-testid="add-chair-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#1E2638] px-3 py-1.5 text-xs text-slate-200 hover:border-[#38BDF8] hover:text-white transition-colors duration-200">
                <Plus className="h-3.5 w-3.5" /> Add chair
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-4" data-testid="chair-grid">
            {(store?.chairs || []).map((chair, i) => (
              <ChairNode key={chair.id} chair={chair} index={i} stage1Min={stage1Min} onClick={() => setSeatChair(chair)} />
            ))}
          </div>
        </div>

        {/* Flags for this store */}
        <div className="surface-card p-6">
          <h2 className="font-display text-lg font-medium">Flagged here</h2>
          <p className="text-xs text-slate-400 mt-1">Services with no matching sale</p>
          <div className="mt-4 space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
            {visibleFlags.map((ev) => (
              <button
                key={ev.id}
                onClick={() => setTimelineEv(ev)}
                data-testid={`floor-flag-${ev.id}`}
                className="w-full text-left rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3 hover:border-red-500/40 transition-colors duration-200 group flex items-center justify-between"
              >
                <div>
                  <div className="text-sm font-medium">{ev.chair_label}</div>
                  <div className="text-xs text-slate-400">{ev.duration_min} min · {ev.classification}</div>
                  <div className="mt-1 inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 text-[10px] font-semibold text-amber-300" data-testid={`flag-confidence-${ev.id}`}>{ev.confidence ?? 94}% confidence</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display" style={{ color: branding.primary_color }}>{store?.network === "justcuts" ? moneyFor(ev.expected_price, store?.country_code) : money(ev.expected_price)}</span>
                  <PlayCircle className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors duration-200" />
                </div>
              </button>
            ))}
            {flagged && visibleFlags.length === 0 && (
              <div className="text-sm text-slate-400 py-6 text-center">No flags at this store.</div>
            )}
          </div>
        </div>
      </div>

      <VideoEvidenceModal event={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
      <ServiceTimeline event={timelineEv} open={!!timelineEv}
        onOpenChange={(v) => !v && setTimelineEv(null)}
        onWatch={(e) => { setTimelineEv(null); setSelected(e); }} />
      <SeatHistoryModal storeId={storeId} chair={seatChair} open={!!seatChair}
        onOpenChange={(v) => !v && setSeatChair(null)}
        onChanged={refreshFloor}
        onRemoved={() => { setSeatChair(null); refreshFloor(); }} />
    </div>
  );
}
